const DEFAULT_COUNTRY_CODE = '972';

/** Strip spaces, dashes, and parentheses from a phone input. */
export function stripPhoneInput(value: string): string {
  return value.replace(/[\s\-().]/g, '').trim();
}

const PHONE_WIRE_FIELD_KEYS = new Set([
  'phone_number',
  'phone_number_2',
  'phone',
  'mobile',
  'cell_phone',
]);

export function isPhoneWireFieldKey(key: string): boolean {
  return PHONE_WIRE_FIELD_KEYS.has(key.trim().toLowerCase().replace(/\s+/g, '_'));
}

/** Remove display formatting before sending phone values to the API. */
export function normalizePhoneForWire(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  }
  return stripPhoneInput(trimmed);
}

/**
 * Normalize user input to E.164 when possible (default country Israel +972).
 * Accepts 05XXXXXXXX, 533333333, 972533333333, +972533333333.
 */
export function normalizePhoneToE164(value: string, defaultCountryCode = DEFAULT_COUNTRY_CODE): string | null {
  const raw = stripPhoneInput(value);
  if (!raw) {
    return null;
  }

  if (raw.startsWith('+')) {
    const digits = raw.slice(1).replace(/\D/g, '');
    if (digits.length < 8 || digits.length > 15) {
      return null;
    }
    return `+${digits}`;
  }

  const digitsOnly = raw.replace(/\D/g, '');
  if (!digitsOnly) {
    return null;
  }

  if (digitsOnly.startsWith(defaultCountryCode)) {
    const rest = digitsOnly.slice(defaultCountryCode.length);
    if (rest.length >= 8 && rest.length <= 12) {
      return `+${defaultCountryCode}${rest}`;
    }
    return null;
  }

  if (digitsOnly.startsWith('0')) {
    const local = digitsOnly.replace(/^0+/, '');
    if (local.length >= 8 && local.length <= 10) {
      return `+${defaultCountryCode}${local}`;
    }
    return null;
  }

  if (digitsOnly.length >= 8 && digitsOnly.length <= 10) {
    return `+${defaultCountryCode}${digitsOnly}`;
  }

  return null;
}

export function isValidE164Phone(value: string): boolean {
  return /^\+[1-9]\d{7,14}$/.test(value);
}

/** Israeli local numbers: 050-123-4567 (3 digits, dash, 3 digits, dash, rest). */
export function formatIsraeliPhoneGrouped(digitsOnly: string): string {
  const digits = digitsOnly.replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  if (digits.length <= 3) {
    return digits;
  }
  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Display Israeli phones with grouping; leaves other international numbers readable. */
export function formatIsraeliPhoneDisplay(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.startsWith('+972')) {
    const local = trimmed.slice(4).replace(/\D/g, '');
    if (local.length >= 8 && local.length <= 10) {
      const withLeadingZero = local.startsWith('0') ? local : `0${local}`;
      return formatIsraeliPhoneGrouped(withLeadingZero);
    }
    return trimmed;
  }
  if (trimmed.startsWith('+')) {
    return trimmed;
  }
  return formatIsraeliPhoneGrouped(stripPhoneInput(trimmed).replace(/\D/g, ''));
}

/** Display form for Israeli mobiles: 050-123-4567 style without country code. */
export function formatPhoneForDisplay(e164: string): string {
  return formatIsraeliPhoneDisplay(e164);
}

/** Format while typing in profile phone inputs (Israeli local numbers). */
export function formatIsraeliPhoneInput(value: string): string {
  const trimmed = value.trim();
  if (trimmed.startsWith('+')) {
    return trimmed;
  }
  const digits = stripPhoneInput(trimmed).replace(/\D/g, '').slice(0, 10);
  return formatIsraeliPhoneGrouped(digits);
}

/** Seed profile phone inputs once — do not re-format on every keystroke. */
export function phoneForProfileEditInput(value: string | null | undefined): string {
  const trimmed = value?.trim();
  if (!trimmed) {
    return '';
  }
  return formatIsraeliPhoneDisplay(trimmed);
}
